/**
 * Helpers de Location para Pronto Atendimento (presencial / remoto / chat).
 * Espelha a lógica enxuta de Form.resolveNonPresentialLocation sem acoplar o wizard.
 */
import { PostLocation } from "@/services/fhir";
import { useFhirData } from "@/utils/useFhirData";
import { ATTENDANCE_CHANNEL_EXTENSION_URL } from "./serviceCategory";
import { modeToAttendanceType } from "./buildProntoAtendimentoSchedule";
import type { ProntoAtendimentoMode } from "./types";

export interface ProntoLocationOption {
  id: string;
  name: string;
}

function isDomicilio(resource: fhir5.Location | undefined) {
  return resource?.extension?.some(
    (ext) =>
      ext.url === ATTENDANCE_CHANNEL_EXTENSION_URL &&
      ext.valueCode === "domicilio",
  );
}

function getChannel(resource: fhir5.Location | undefined) {
  return resource?.extension?.find(
    (ext) => ext.url === ATTENDANCE_CHANNEL_EXTENSION_URL,
  )?.valueCode;
}

/** Locations presenciais ativas da organização (não virtual, não domicílio). */
export function listPresencialLocations(
  locationBundleEntries: fhir5.BundleEntry<fhir5.Location>[] | undefined,
): ProntoLocationOption[] {
  return (
    locationBundleEntries
      ?.filter(
        (e) =>
          e.resource?.status === "active" &&
          !useFhirData.virtualService(e.resource) &&
          !isDomicilio(e.resource),
      )
      .map((e) => ({
        id: e.resource?.id || "",
        name: e.resource?.name || "Local",
      }))
      .filter((l) => !!l.id) || []
  );
}

/**
 * Resolve Location para remoto/chat: reutiliza existente ou retorna dados para criar.
 */
export function resolveVirtualLocationData(
  mode: Exclude<ProntoAtendimentoMode, "presencial">,
  locationBundleEntries: fhir5.BundleEntry<fhir5.Location>[] | undefined,
): { locationId?: string; name: string; serviceOnline: true; attendanceType: string } {
  const attendanceType = modeToAttendanceType(mode);

  const existing = locationBundleEntries?.find((loc) => {
    if (loc?.resource?.status !== "active") return false;
    if (!useFhirData.virtualService(loc.resource)) return false;
    const channel = getChannel(loc.resource);
    if (attendanceType === "teleatendimento") {
      return !channel || channel === "teleatendimento";
    }
    return channel === "chat";
  });

  if (existing?.resource?.id) {
    return {
      locationId: existing.resource.id,
      name: existing.resource.name || (mode === "chat" ? "Chat" : "Teleatendimento"),
      serviceOnline: true,
      attendanceType,
    };
  }

  return {
    name: mode === "chat" ? "Chat" : "Teleatendimento",
    serviceOnline: true,
    attendanceType,
  };
}

export interface EnsureLocationResult {
  id: string;
  name: string;
}

/** Garante Location (reusa ou cria via PostLocation). */
export async function ensureProntoAtendimentoLocation(params: {
  mode: ProntoAtendimentoMode;
  organizationId: string;
  locationEntries: fhir5.BundleEntry<fhir5.Location>[] | undefined;
  /** Presencial: id selecionado OU dados para criar */
  selectedLocationId?: string;
  newLocation?: {
    name: string;
    city?: string;
    state?: string;
    cep?: string;
    street?: string;
    number?: string;
    complement?: string;
  };
  refetchLocation?: () => void;
}): Promise<EnsureLocationResult> {
  const { mode, organizationId, locationEntries } = params;

  if (mode === "presencial") {
    if (params.selectedLocationId) {
      const found = listPresencialLocations(locationEntries).find(
        (l) => l.id === params.selectedLocationId,
      );
      if (found) return found;
      throw new Error("Localização presencial não encontrada.");
    }

    const name = params.newLocation?.name?.trim();
    if (!name) throw new Error("Informe o nome da nova localização.");

    const created = await PostLocation(
      {
        name,
        attendanceType: "presencial",
        serviceOnline: false,
        city: params.newLocation?.city,
        state: params.newLocation?.state,
        cep: params.newLocation?.cep,
        street: params.newLocation?.street,
        number: params.newLocation?.number,
        complement: params.newLocation?.complement,
      },
      organizationId,
    );
    params.refetchLocation?.();
    return { id: created.id, name: created.name || name };
  }

  // Remoto / Chat
  const resolved = resolveVirtualLocationData(
    mode,
    locationEntries,
  );
  if (resolved.locationId) {
    return { id: resolved.locationId, name: resolved.name };
  }

  const created = await PostLocation(
    {
      name: resolved.name,
      serviceOnline: true,
      attendanceType: resolved.attendanceType,
    },
    organizationId,
  );
  params.refetchLocation?.();
  return { id: created.id, name: created.name || resolved.name };
}
